import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Slider,
  Typography,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  Alert,
  Grid,
  Tooltip,
  TextField,
  Button,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Speed,
  Replay10,
  Forward10,
  Download,
} from '@mui/icons-material';
import {
  getSessionsWithAudio,
  getAudioFileUrl,
  getSessionAudio,
  getAudioSegments,
  type AudioSession,
  type AudioSegment as APIAudioSegment,
} from '../../api/audioApi';

interface AudioSegment {
  id: string;
  startTime: number;
  endTime: number;
  speaker: string;
  text: string;
}

interface AudioPlaybackProps {
  sessionId?: string;
  audioUrl?: string;
  segments?: AudioSegment[];
}

interface AudioSessionFilters {
  patient_name?: string;
  mrn?: string;
  session_id?: string;
  visit_id?: string;
  visit_date?: string;
}

export default function AudioPlayback({ sessionId: propSessionId, audioUrl, segments = [] }: AudioPlaybackProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSegment, setCurrentSegment] = useState<AudioSegment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Real data state
  const [allSessions, setAllSessions] = useState<AudioSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(propSessionId || null);
  const [audioSegments, setAudioSegments] = useState<AudioSegment[]>([]);
  const [draftFilters, setDraftFilters] = useState<AudioSessionFilters>({});
  const [appliedFilters, setAppliedFilters] = useState<AudioSessionFilters>({});

  // Load all sessions on mount (if no sessionId provided)
  useEffect(() => {
    if (!propSessionId) {
      loadAllSessions(appliedFilters);
    }
  }, [propSessionId, appliedFilters]);

  // Load audio for selected session
  useEffect(() => {
    if (selectedSessionId) {
      loadSessionAudio(selectedSessionId);
    }
  }, [selectedSessionId]);

  // Update current segment based on time
  useEffect(() => {
    const segment = audioSegments.find(
      (seg) => currentTime >= seg.startTime && currentTime < seg.endTime
    );
    setCurrentSegment(segment || null);
  }, [currentTime, audioSegments]);

  const loadAllSessions = async (filters: AudioSessionFilters) => {
    try {
      setIsLoading(true);
      setError(null);
      const sessionsByPatient = await getSessionsWithAudio(filters);

      // Flatten all sessions from all patients and sort recent first
      const flatSessions = Object.values(sessionsByPatient).flat();
      flatSessions.sort((a, b) => {
        const tA = a.created_at
          ? new Date(a.created_at).getTime()
          : new Date(`${a.session_date || ''}T${a.session_time || '00:00:00'}`).getTime();
        const tB = b.created_at
          ? new Date(b.created_at).getTime()
          : new Date(`${b.session_date || ''}T${b.session_time || '00:00:00'}`).getTime();
        return tB - tA;
      });
      setAllSessions(flatSessions);

      // Auto-select first session if available (guard against missing session_id)
      const firstId = flatSessions[0]?.session_id ?? (flatSessions[0] as { id?: string } | undefined)?.id;
      const selectedStillExists = !!selectedSessionId && flatSessions.some((s) => (s?.session_id ?? (s as { id?: string }).id) === selectedSessionId);
      if (!selectedStillExists && firstId) {
        setSelectedSessionId(firstId);
      } else if (flatSessions.length === 0) {
        setError('No audio recordings found');
        setSelectedSessionId(null);
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setError('Failed to load audio sessions');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSessionAudio = async (sessionId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Get audio metadata (this triggers generation if needed)
      const metadata = await getSessionAudio(sessionId);

      // Load audio into player
      if (audioRef.current) {
        audioRef.current.src = getAudioFileUrl(sessionId);
        audioRef.current.load();
      }

      // Load transcript segments
      try {
        const segmentsData = await getAudioSegments(sessionId);
        const mappedSegments: AudioSegment[] = segmentsData.segments.map((seg) => ({
          id: seg.id,
          startTime: seg.startTime,
          endTime: seg.endTime,
          speaker: seg.speaker,
          text: seg.text,
        }));
        setAudioSegments(mappedSegments);
      } catch (segErr) {
        console.warn('Failed to load segments:', segErr);
        // Continue without segments
        setAudioSegments([]);
      }

      setIsLoading(false);
    } catch (err) {
      console.error('Failed to load session audio:', err);
      setError('Failed to load audio for this session');
      setIsLoading(false);
    }
  };

  // Audio element event handlers
  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleAudioError = () => {
    setError('Failed to load audio file');
    setIsLoading(false);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      if (currentTime >= duration) {
        audioRef.current.currentTime = 0;
      }
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeChange = (_event: Event, newValue: number | number[]) => {
    const time = newValue as number;
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
    setCurrentTime(time);
  };

  const handleVolumeChange = (_event: Event, newValue: number | number[]) => {
    const vol = newValue as number;
    if (audioRef.current) {
      audioRef.current.volume = vol;
    }
    setVolume(vol);
    setIsMuted(vol === 0);
  };

  const handleMuteToggle = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
    }
    setIsMuted(!isMuted);
  };

  const handleSpeedToggle = () => {
    const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % speeds.length;
    const newSpeed = speeds[nextIndex];
    if (audioRef.current) {
      audioRef.current.playbackRate = newSpeed;
    }
    setPlaybackRate(newSpeed);
  };

  const handleRewind = () => {
    const newTime = Math.max(0, currentTime - 10);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
    setCurrentTime(newTime);
  };

  const handleForward = () => {
    const newTime = Math.min(duration, currentTime + 10);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
    setCurrentTime(newTime);
  };

  const handleSegmentClick = (segment: AudioSegment) => {
    if (audioRef.current) {
      audioRef.current.currentTime = segment.startTime;
      audioRef.current.play();
    }
    setCurrentTime(segment.startTime);
    setIsPlaying(true);
  };

  const handleDownload = async () => {
    if (!selectedSessionId) return;
    try {
      const url = getAudioFileUrl(selectedSessionId);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `session_${selectedSessionId.substring(0, 8)}_audio.wav`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Failed to download audio:', err);
      setError('Failed to download audio file');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSpeakerColor = (speaker: string) => {
    return speaker === 'SPEAKER_00' ? '#1976d2' : '#e91e63';
  };

  const handleFilterInputChange = (key: keyof AudioSessionFilters, value: string) => {
    setDraftFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilters = () => {
    const cleaned: AudioSessionFilters = {};
    Object.entries(draftFilters).forEach(([k, v]) => {
      const value = String(v ?? '').trim();
      if (value) {
        cleaned[k as keyof AudioSessionFilters] = value;
      }
    });
    setAppliedFilters(cleaned);
  };

  const clearFilters = () => {
    setDraftFilters({});
    setAppliedFilters({});
  };

  return (
    <Grid container spacing={2}>
      {/* Left Panel: Audio Player and Controls */}
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom>
            Audio Playback
          </Typography>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Session Selector (if no sessionId provided as prop) */}
          {!propSessionId && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Search Recordings
              </Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Patient Name"
                    size="small"
                    fullWidth
                    value={draftFilters.patient_name || ''}
                    onChange={(e) => handleFilterInputChange('patient_name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="MRN"
                    size="small"
                    fullWidth
                    value={draftFilters.mrn || ''}
                    onChange={(e) => handleFilterInputChange('mrn', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Session ID"
                    size="small"
                    fullWidth
                    value={draftFilters.session_id || ''}
                    onChange={(e) => handleFilterInputChange('session_id', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Visit ID"
                    size="small"
                    fullWidth
                    value={draftFilters.visit_id || ''}
                    onChange={(e) => handleFilterInputChange('visit_id', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Visit Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={draftFilters.visit_date || ''}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => handleFilterInputChange('visit_date', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button variant="contained" size="small" onClick={applyFilters}>
                    Apply Filters
                  </Button>
                  <Button variant="outlined" size="small" onClick={clearFilters}>
                    Clear
                  </Button>
                </Grid>
              </Grid>

              {allSessions.length > 0 ? (
                <>
              <Typography variant="subtitle2" gutterBottom>
                Select Recording
              </Typography>
              <Select
                value={selectedSessionId || ''}
                onChange={(e) => setSelectedSessionId(e.target.value)}
                fullWidth
                size="small"
              >
                {allSessions.map((session, index) => {
                  const sid = session?.session_id ?? (session as { id?: string }).id;
                  if (sid == null) return null;
                  return (
                    <MenuItem key={sid} value={sid}>
                      {(session.patient_name || 'Unknown patient')} ({session.mrn || 'No MRN'}) | Visit {session.visit_id ?? '-'} {session.visit_date ?? ''} | Session {String(sid).substring(0, 8)}... | {Math.round(Number(session?.duration) || 0)}s
                    </MenuItem>
                  );
                })}
              </Select>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recordings match the selected filters.
                </Typography>
              )}
            </Box>
          )}

          {isLoading ? (
            <Box sx={{ py: 4 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                Loading audio... This may take a moment if the audio needs to be generated.
              </Typography>
            </Box>
          ) : !selectedSessionId ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No audio recording selected
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Timeline Slider */}
              <Box sx={{ px: 2, pt: 2 }}>
                <Slider
                  value={currentTime}
                  max={duration}
                  onChange={handleTimeChange}
                  sx={{
                    '& .MuiSlider-thumb': {
                      width: 16,
                      height: 16,
                    },
                    '& .MuiSlider-track': {
                      height: 6,
                    },
                    '& .MuiSlider-rail': {
                      height: 6,
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption">{formatTime(currentTime)}</Typography>
                  <Typography variant="caption">{formatTime(duration)}</Typography>
                </Box>
              </Box>

              {/* Controls */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  mt: 2,
                }}
              >
                <IconButton onClick={handleRewind} sx={{ color: '#1976d2' }}>
                  <Replay10 />
                </IconButton>

                <IconButton
                  onClick={handlePlayPause}
                  sx={{
                    bgcolor: '#1976d2',
                    color: 'white',
                    width: 56,
                    height: 56,
                    '&:hover': { bgcolor: '#1565c0' },
                  }}
                >
                  {isPlaying ? <Pause sx={{ fontSize: 32 }} /> : <PlayArrow sx={{ fontSize: 32 }} />}
                </IconButton>

                <IconButton onClick={handleForward} sx={{ color: '#1976d2' }}>
                  <Forward10 />
                </IconButton>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                  <IconButton onClick={handleMuteToggle} size="small">
                    {isMuted || volume === 0 ? <VolumeOff /> : <VolumeUp />}
                  </IconButton>
                  <Slider
                    value={isMuted ? 0 : volume}
                    max={1}
                    step={0.1}
                    onChange={handleVolumeChange}
                    sx={{ width: 100 }}
                  />
                </Box>

                <IconButton onClick={handleSpeedToggle} sx={{ ml: 1 }}>
                  <Speed />
                </IconButton>
                <Chip
                  label={`${playbackRate}x`}
                  size="small"
                  sx={{ bgcolor: '#1976d2', color: 'white', minWidth: 50 }}
                />

                <Tooltip title="Download audio file">
                  <IconButton
                    onClick={handleDownload}
                    disabled={!selectedSessionId}
                    sx={{
                      ml: 1,
                      color: '#1976d2',
                      border: '1px solid #1976d2',
                      '&:hover': { bgcolor: '#1976d2', color: 'white' },
                    }}
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
                
              </Box>

              {/* Live Transcript - Current Segment Display */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Live Transcript
                </Typography>
                {currentSegment ? (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      borderLeft: `4px solid ${getSpeakerColor(currentSegment.speaker)}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip
                        label={currentSegment.speaker === 'SPEAKER_00' ? 'Doctor' : 'Patient'}
                        size="small"
                        sx={{
                          bgcolor: getSpeakerColor(currentSegment.speaker),
                          color: 'white',
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(currentSegment.startTime)} - {formatTime(currentSegment.endTime)}
                      </Typography>
                    </Box>
                    <Typography variant="body2">{currentSegment.text}</Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No segment playing
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* Right Panel: Transcript Timeline */}
      <Grid item xs={12} md={5}>
        {audioSegments.length > 0 && (
          <Paper sx={{ p: 2, maxHeight: '700px', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              Transcript Timeline ({audioSegments.length})
            </Typography>
            <List
              sx={{
                flex: 1,
                overflowY: 'auto',
                maxHeight: '620px',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#bdbdbd',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#9e9e9e',
                  },
                },
              }}
            >
              {audioSegments.map((segment) => (
                <ListItem
                  key={segment.id}
                  onClick={() => handleSegmentClick(segment)}
                  sx={{
                    cursor: 'pointer',
                    borderLeft: `4px solid ${getSpeakerColor(segment.speaker)}`,
                    mb: 1,
                    bgcolor: currentSegment?.id === segment.id ? '#e3f2fd' : '#fafafa',
                    borderRadius: 1,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip
                          label={segment.speaker === 'SPEAKER_00' ? 'Doctor' : 'Patient'}
                          size="small"
                          sx={{
                            bgcolor: getSpeakerColor(segment.speaker),
                            color: 'white',
                            height: 20,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(segment.startTime)} - {formatTime(segment.endTime)}
                        </Typography>
                      </Box>
                    }
                    secondary={segment.text}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Grid>

      {/* Audio element */}
      <audio
        ref={audioRef}
        onLoadedMetadata={handleAudioLoadedMetadata}
        onTimeUpdate={handleAudioTimeUpdate}
        onEnded={handleAudioEnded}
        onError={handleAudioError}
        style={{ display: 'none' }}
      />
    </Grid>
  );
}
